import { trackInteraction } from 'client/javascript/analytics';
import { logError } from 'client/javascript/util/logger';
import { getLocale } from 'client/javascript/util/locale';
import User, { USER_STORAGE_KEY_MAP } from 'shared/storage/user';

const SELECTORS = {
  panel: '#panel_button_filter',
  panelTeamSize: '.panel-button__team-size',
  teamSizeCounter: '.panel-button__counter',
  increaseButton: '.panel-button__increase-count',
  reduceButton: '.panel-button__reduce-count',
  displayCount: '.panel__display-count',
  counterNumber: '.panel__display-counter',
  inputCounter: '.panel__display-input',
  applyButton: '.panel-button__apply',
  clearButton: '.panel-button__clear',
  blueButton: '.panel-button__clear--blue',
  stickPanel: '.panel-button--sticky',
  panelFilterPrice: '.panel-filter__price',
  pageFooter: '.page-footer'
};

const CONSTANTS = {
  KEYCODE_ENTER: 13,
  MAX_COUNT: 99,
  MIN_COUNT: 1,
  MIN_KEYCODE_OF_NUMBER: 48,
  MAX_KEYCODE_OF_NUMBER: 57
};

const getClosestLocation = async ({ marketId, capacity }) => {
  let queryString = 'monthly=1';
  queryString += capacity ? `&minCapacity=${capacity}` : '';

  try {
    const response = await fetch(`/${getLocale()}/workspace/standard-office/${marketId}?${queryString}`);
    return response.json();
  } catch (error) {
    logError('Failed to fetch closest location: ', error);
  }
};

export class MemberCounter {
  static create(options) {
    return new MemberCounter(options);
  }

  constructor({ interactionLabel, onScroll }) {
    this.interactionLabel = interactionLabel;
    // Init dom elements
    this.footer = document.querySelector(SELECTORS.pageFooter);
    this.panel = document.querySelector(SELECTORS.panel);
    this.panelTeamSize = document.querySelector(SELECTORS.panelTeamSize);
    this.teamSizeCounter = document.querySelector(SELECTORS.teamSizeCounter);
    this.countOfTeam = this.panelTeamSize.querySelector(SELECTORS.displayCount);
    this.counterNumber = this.teamSizeCounter.querySelector(SELECTORS.counterNumber);
    this.inputCounter = this.teamSizeCounter.querySelector(SELECTORS.inputCounter);
    this.increaseButton = this.teamSizeCounter.querySelector(SELECTORS.increaseButton);
    this.reduceButton = this.teamSizeCounter.querySelector(SELECTORS.reduceButton);
    this.clearButton = this.teamSizeCounter.querySelector(SELECTORS.clearButton);
    this.applyButton = this.teamSizeCounter.querySelector(SELECTORS.applyButton);
    this.panelFilterPrice = document.querySelector(SELECTORS.panelFilterPrice);
    // Initial counter and market
    this.isTeamSizeCounter = false;
    this.marketId = null;
    this.countOfTeam.textContent = CONSTANTS.MIN_COUNT;
    this.maxCapacity = CONSTANTS.MAX_COUNT;
    // Init eventListenters
    this.panelTeamSize.addEventListener('click', this.handleTeamSizeClick);
    this.inputCounter.addEventListener('keypress', this.typeCountHandler);
    this.increaseButton.addEventListener('click', this.increaseCount);
    this.reduceButton.addEventListener('click', this.reduceCount);
    this.clearButton.addEventListener('click', this.clearCount);
    this.applyButton.addEventListener('click', this.applyCount);
    if (typeof onScroll === 'function') {
      onScroll();
    } else {
      document.addEventListener('scroll', this.stickScroll);
    }
  }

  setPrice = async () => {
    this.panelFilterPrice.textContent = '-';
    const [result] = await getClosestLocation({ marketId: this.marketId, capacity: this.countOfTeam.textContent });
    this.panelFilterPrice.textContent = result;
  };

  stickScroll = () => {
    const scrollPosition = Math.round(window.scrollY + window.innerHeight);
    const trigerPostion = Math.round(document.body.offsetHeight - this.footer.offsetHeight);
    if (scrollPosition > trigerPostion + this.panel.offsetHeight) {
      this.panel.classList.add(SELECTORS.stickPanel.slice(1));
    }
    if (scrollPosition < trigerPostion) {
      this.panel.classList.remove(SELECTORS.stickPanel.slice(1));
    }
  };

  setMarketId = id => {
    this.marketId = id;
  };

  togglePanelTeamSize = () => {
    this.isTeamSizeCounter = !this.isTeamSizeCounter;
    this.toggleClearColor();
    if (this.isTeamSizeCounter) {
      this.counterInit();
      this.teamSizeCounter.style.display = 'block';
      return document.addEventListener('click', this.outsideClickHandler);
    }

    document.removeEventListener('click', this.outsideClickHandler);
    this.teamSizeCounter.style.display = 'none';
  };

  toggleClearColor = () => {
    if (this.inputCounter.value > CONSTANTS.MIN_COUNT) {
      this.clearButton.classList.add(SELECTORS.blueButton.slice(1));
    } else {
      this.clearButton.classList.remove(SELECTORS.blueButton.slice(1));
    }
  };

  counterInit = () => {
    this.inputCounter.value = this.countOfTeam.textContent;
  };

  increaseCount = () => {
    const count = +this.inputCounter.value + 1;
    if (count <= this.maxCapacity) {
      this.inputCounter.value = count;
    }
    this.toggleClearColor();
  };

  clearCount = () => {
    this.inputCounter.value = CONSTANTS.MIN_COUNT;
    this.toggleClearColor();
  };

  reduceCount = () => {
    const count = +this.inputCounter.value - 1;
    if (count >= CONSTANTS.MIN_COUNT) {
      this.inputCounter.value = count;
    }
    this.toggleClearColor();
  };

  applyCount = () => {
    if (Number(this.inputCounter.value) > Number(this.maxCapacity)) {
      this.inputCounter.value = this.maxCapacity;
    }
    this.countOfTeam.textContent = this.inputCounter.value;
    User.saveValue(USER_STORAGE_KEY_MAP.capacity, this.inputCounter.value);

    this.togglePanelTeamSize();
    this.toggleClearColor();
    this.setPrice();
    trackInteraction({ label: this.interactionLabel, filterValue: +this.inputCounter.value });
  };

  handleTeamSizeClick = event => {
    if (event) {
      event.preventDefault();
    }

    if (!event.target.closest(SELECTORS.teamSizeCounter)) {
      this.togglePanelTeamSize();
    }
  };

  isNumberKey = event => {
    if (!(event.keyCode >= CONSTANTS.MIN_KEYCODE_OF_NUMBER && event.keyCode <= CONSTANTS.MAX_KEYCODE_OF_NUMBER)) {
      event.preventDefault();
      return false;
    }
  };

  typeCountHandler = event => {
    if (event.target.value > this.maxCapacity) {
      this.inputCounter.value = this.maxCapacity;
    }

    if (event.keyCode === CONSTANTS.KEYCODE_ENTER) {
      this.applyCount();
    }

    this.toggleClearColor();
    this.isNumberKey(event);
  };

  outsideClickHandler = event => {
    if (!event.target.closest(SELECTORS.panelTeamSize)) {
      this.togglePanelTeamSize();
    }
  };
}
